/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
} from "react";

import { FULLY_AUTHENTICATED } from "../../constants";
import {
  ActionParams,
  DynamicMessageParams,
  NavigationParams,
  ProcessParams,
  ViewParams,
} from "../types";

// Function to track page views
const view = (pageview: ViewParams) => {
  window.nbs_ddl.page = { ...pageview };
  window.wa_view();
};

// Function to track actions
const action = (
  type: ActionParams["type"],
  element: ActionParams["element"]
) => {
  window.nbs_ddl.action.unshift({ type, element });
  window.wa_action();
};

// Function to set navigation
const setNavigation = (viewNavigation: NavigationParams) => {
  const { type, identifier } = viewNavigation;
  window.nbs_ddl.page.navigation = [];
  window.nbs_ddl.page.navigation.unshift({ type, identifier });
};

// Function to set dynamic messages
const setDynamicMessage = (viewMessage: DynamicMessageParams) => {
  const { text, cause } = viewMessage;
  window.nbs_ddl.page.message = [];
  window.nbs_ddl.page.message.unshift({ text, cause });
};

// Function to set process
const process = (processData: ProcessParams | ProcessParams[]) => {
  window.nbs_ddl.process = [];
  const processEntries = Array.isArray(processData)
    ? processData
    : [processData];

  processEntries.forEach(({ category, identifier, value }) => {
    const isProcessEntryPresent = window.nbs_ddl.process.some(
      (item: ProcessParams) =>
        item.category === category &&
        item.identifier === identifier &&
        item.value === value
    );

    if (!isProcessEntryPresent) {
      window.nbs_ddl.process.unshift({
        category,
        identifier,
        value,
      });
    }
  });
};

const reset = () => {
  window.nbs_ddl = { page: {}, action: [], process: [] };
};

// Create context with a default value of undefined
const TrackingContext = createContext<
  | {
      view: (pageview: ViewParams) => void;
      action: (
        type: ActionParams["type"],
        element: ActionParams["element"]
      ) => void;
      setNavigation: (viewNavigation: NavigationParams) => void;
      setDynamicMessage: (viewMessage: DynamicMessageParams) => void;
      process: (processData: ProcessParams | ProcessParams[]) => void;
      reset: () => void;
    }
  | undefined
>(undefined);

const TrackingProvider = (props: { children: ReactNode }) => {
  const ANALYTICS_SCRIPT_URL = window.configuration?.ANALYTICS_SCRIPT_URL;

  useLayoutEffect(() => {
    if (document.querySelector(`script[src="${ANALYTICS_SCRIPT_URL}"]`)) return;
    window.wa_action = () => null;
    window.wa_view = () => null;
    window.nbs_ddl = window.nbs_ddl || { page: {}, action: [] };

    const script = document.createElement("script");
    script.setAttribute("src", ANALYTICS_SCRIPT_URL);
    script.setAttribute("async", "true");
    document.querySelector("body")?.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  useEffect(() => {
    view({
      name: "/",
      cis_number: "",
      authentication_status: FULLY_AUTHENTICATED,
    });
  }, []);

  const value = React.useMemo(
    () => ({
      action,
      view,
      reset,
      setDynamicMessage,
      process,
      setNavigation,
    }),
    []
  );

  return <TrackingContext.Provider value={value} {...props} />;
};

function useTrackingContext() {
  const context = useContext(TrackingContext);
  if (context === undefined) {
    throw new Error("useTrackingContext must be used within a Provider");
  }
  return context;
}

export { TrackingProvider, TrackingContext, useTrackingContext };
